import React, { useState } from 'react';
import { InterventionPayload } from '../hooks/useProjectStream';

interface InterventionModalProps {
  intervention: InterventionPayload;
  onSubmit: (action: 'approve' | 'reject' | 'modify', modifications?: string) => void;
}

export const InterventionModal: React.FC<InterventionModalProps> = ({ intervention, onSubmit }) => {
  const [modifications, setModifications] = useState('');
  const [mode, setMode] = useState<'review' | 'modify'>('review');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-amber-950/30 dark:bg-amber-900/30 backdrop-blur-md animate-in fade-in duration-300">
      <div className="w-full max-w-5xl bg-white dark:bg-[#121212] rounded-3xl shadow-2xl border border-amber-200 dark:border-amber-900/50 flex flex-col md:flex-row overflow-hidden max-h-[90vh] animate-in zoom-in-95 duration-400">
        
        {/* Left Panel: Asset Preview */}
        <div className="w-full md:w-3/5 bg-gray-50 dark:bg-[#1a1a1a] p-8 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest font-sans">Neura Awaiting Approval</h3>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{intervention.title}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            {intervention.description}
          </p>

          {intervention.assetPreview && (
            <div className="flex-1 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 overflow-y-auto font-mono text-sm text-gray-800 dark:text-gray-300 shadow-sm relative group">
               <div className="absolute top-4 right-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded">
                 {intervention.assetType || 'Text'} Preview
               </div>
               <pre className="whitespace-pre-wrap">{intervention.assetPreview}</pre>
            </div>
          )}
        </div>

        {/* Right Panel: Intervention Controls */}
        <div className="w-full md:w-2/5 p-8 flex flex-col bg-white dark:bg-[#121212]">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-6 uppercase tracking-widest">Operational Decision</h3>
          
          {mode === 'review' ? (
            <div className="flex flex-col gap-4 flex-1 justify-center">
              <button 
                onClick={() => onSubmit('approve')}
                className="w-full py-4 rounded-xl bg-black dark:bg-white text-white dark:text-black font-bold text-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                Approve & Resume Workflow
              </button>
              
              <button 
                onClick={() => setMode('modify')}
                className="w-full py-4 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                Modify Output
              </button>

              <button 
                onClick={() => onSubmit('reject')}
                className="w-full py-4 rounded-xl border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 font-bold text-sm hover:bg-red-50 dark:hover:bg-red-950/20 transition-all flex items-center justify-center gap-2 mt-4"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                Reject & Halt
              </button>
            </div>
          ) : (
            <div className="flex flex-col flex-1 animate-in fade-in slide-in-from-right-4 duration-300">
              <label className="text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-widest">Feedback Instructions</label>
              <textarea 
                autoFocus
                value={modifications}
                onChange={(e) => setModifications(e.target.value)}
                placeholder="Instruct Neura on what needs to change..."
                className="flex-1 w-full bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 text-sm text-gray-900 dark:text-white outline-none focus:border-black dark:focus:border-white resize-none mb-6"
              />
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setMode('review')}
                  className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-bold text-xs hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => onSubmit('modify', modifications)}
                  disabled={!modifications.trim()}
                  className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-bold text-xs hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  Submit Revision
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
